import { createContextWithHook } from '@chilipiper/utils'
import React, { useId, useRef } from 'react'
import { useCheckbox } from 'react-aria'
import { useToggleState } from 'react-stately'
import styled, { css } from 'styled-components'
import invariant from 'tiny-invariant'
import { typeValidator } from '@chilipiper/utils'
import { Box, Flex, ScreenReaderOnly, Text } from '../../../core-components'
import { useFocusVisible } from '../../hooks'
import { OmitInternalProperties } from '../../types'
import { EndContent, EndContentProvider, StartContent, StartContentProvider } from '../../data'
import { CoreCheckbox } from './CoreCheckbox'

type CommonProps = {
  /** Used only in storybook to present focused state, don't use in production code! */
  INTERNAL_isFocusVisible?: boolean
  'data-id'?: string
  'data-test-id'?: string
  isDisabled?: boolean
  label: React.ReactNode
  name?: string
}

/** The order is unchecked -> checked -> mixed -> unchecked */
type CheckboxState = 'checked' | 'mixed' | 'unchecked'

type SimpleStateProps = CommonProps & {
  isChecked: boolean
  onChange: (value: boolean) => void
  state?: never
}

type MixedStateProps = CommonProps & {
  isChecked?: never
  onChange: (value: CheckboxState) => void
  state: CheckboxState
}

type Props = SimpleStateProps | MixedStateProps

export const CheckboxInternal = (props: Props) => {
  const {
    INTERNAL_isFocusVisible,
    'data-id': dataId,
    'data-test-id': testId,
    state,
    isDisabled = false,
    label,
    name,
    onChange,
  } = parseProps(props)
  const { description } = parseLabel(label)
  const toggleState = useToggleState({
    isSelected: state === 'checked',
    onChange: isChecked => {
      switch (state) {
        case 'checked':
          if (!isChecked) {
            onChange('mixed')
          }
          break
        case 'mixed':
          onChange('unchecked')
          break
        case 'unchecked':
          if (isChecked) {
            onChange('checked')
          }
          break
      }
    },
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const id = useId()
  const labelId = `checkbox-label-${id}`
  const descriptionId = `checkbox-description-${id}`
  const { inputProps } = useCheckbox(
    {
      isDisabled,
      isIndeterminate: state === 'mixed',
      'aria-labelledby': labelId,
      'aria-describedby': description ? descriptionId : undefined,
      name,
    },
    toggleState,
    inputRef
  )
  const { isFocusVisible, focusProps } = useFocusVisible(INTERNAL_isFocusVisible)
  return (
    <Label
      as='label'
      display='flex'
      data-id={dataId}
      data-test-id={testId}
      px={6}
      py={4}
      isChecked={state !== 'unchecked'}
      isDisabled={isDisabled}
      isFocused={isFocusVisible}
      cursor={isDisabled ? 'default' : 'pointer'}
      position={
        'relative' /* To handle https://filip-wachowiak.vercel.app/blog/default-absolute-positioning-in-a-scroll-container */
      }
    >
      <ScreenReaderOnly>
        <input {...inputProps} {...focusProps} ref={inputRef} />
      </ScreenReaderOnly>
      <CoreCheckbox
        isChecked={state === 'checked'}
        isMixedCheck={state === 'mixed'}
        isDisabled={isDisabled}
      />
      <LabelBaseProvider
        value={{ isChecked: state !== 'unchecked', isDisabled, labelId, descriptionId }}
      >
        {label}
      </LabelBaseProvider>
    </Label>
  )
}

const parseProps = (props: Props) => {
  if ('state' in props) {
    return props
  }
  const { isChecked, onChange, ...rest } = props
  return {
    ...rest,
    state: isChecked ? 'checked' : 'unchecked',
    onChange: (value: CheckboxState) => onChange(value === 'checked'),
  } as const
}

const parseLabel = (label: React.ReactNode) => {
  invariant(
    typeValidator(CheckboxLabel)(label),
    `Checkbox: label must be a Checkbox.Label component`
  )
  return {
    text: label.props.children,
    description: label.props.description,
  }
}

const { LabelBaseProvider, useLabelContext } = createContextWithHook<{
  descriptionId: string
  isChecked: boolean
  isDisabled: boolean
  labelId: string
}>()('Label')

type LabelProps = {
  children: string
  description?: string
  endContent?: React.ReactNode
  startContent?: React.ReactNode
}
const CheckboxLabel = ({ startContent, endContent, children, description }: LabelProps) => {
  invariant(
    startContent === undefined || typeValidator(CheckboxStartContent)(startContent),
    `Checkbox: startContent must be a Checkbox.StartContent component`
  )
  invariant(
    endContent === undefined || typeValidator(CheckboxEndContent)(endContent),
    'endContent must be a Checkbox.EndContent component'
  )
  const { isDisabled, isChecked, labelId, descriptionId } = useLabelContext()
  return (
    <Box ml={4}>
      <Flex alignItems='center' id={labelId} gap={2}>
        {startContent && (
          <StartContentProvider value={{ iconColor: 'icon/indicator' }}>
            {startContent}
          </StartContentProvider>
        )}
        <Text
          textStyle={isChecked ? 'item-selected' : 'item-unselected'}
          color={isDisabled ? 'text/form-value-disabled' : 'text/list-item'}
          mr={endContent ? 1 : 0}
        >
          {children}
        </Text>
        {endContent && <EndContentProvider value={{}}>{endContent}</EndContentProvider>}
      </Flex>
      {description && (
        <Text
          id={descriptionId}
          textStyle='body-multiline'
          color={isDisabled ? 'text/form-value-disabled' : 'text/body-moderate'}
          display='block'
          mt={2}
        >
          {description}
        </Text>
      )}
    </Box>
  )
}

type PublicProps =
  | OmitInternalProperties<SimpleStateProps>
  | OmitInternalProperties<MixedStateProps>

export const CheckboxPublic = (props: PublicProps) => <CheckboxInternal {...props} />

const CheckboxStartContent = (props: React.ComponentPropsWithoutRef<typeof StartContent>) => (
  <StartContent {...props} />
)
const CheckboxEndContent = (props: React.ComponentPropsWithoutRef<typeof EndContent>) => (
  <EndContent {...props} />
)

CheckboxInternal.Label = CheckboxLabel
CheckboxInternal.StartContent = CheckboxStartContent
CheckboxInternal.EndContent = CheckboxEndContent
CheckboxPublic.Label = CheckboxLabel
CheckboxPublic.StartContent = CheckboxStartContent
CheckboxPublic.EndContent = CheckboxEndContent

const Label = styled(Box)<{ isChecked: boolean; isDisabled: boolean; isFocused: boolean }>`
  background: ${({ theme, isDisabled, isChecked }) =>
    theme.colors[getNormalBackgroundColor({ isDisabled, isChecked })]};
  box-shadow: ${({ theme, isChecked }) => theme.shadows[getNormalShadow({ isChecked })]};

  :hover {
    background: ${({ theme, isDisabled }) =>
      isDisabled ? undefined : theme.colors['bg/list-item-hover']};
  }
  :focus {
    background: ${({ theme, isDisabled }) =>
      isDisabled ? undefined : theme.colors['bg/list-item-hover']};
    box-shadow: ${({ theme, isDisabled, isChecked }) =>
      isDisabled ? undefined : theme.shadows[getFocusBorder({ isChecked })]};
  }
  ${({ theme, isFocused, isDisabled, isChecked }) =>
    isFocused &&
    css`
      background: ${() => (isDisabled ? undefined : theme.colors['bg/list-item-hover'])};
      box-shadow: ${() => (isDisabled ? undefined : theme.shadows[getFocusBorder({ isChecked })])};
    `}
`

const getNormalBackgroundColor = (props: { isChecked: boolean; isDisabled: boolean }) => {
  if (props.isDisabled) {
    return 'bg/disabled'
  }
  if (props.isChecked) {
    return 'bg/list-item-selected'
  }
  return 'bg/list-item-unselected'
}

const getNormalShadow = (props: { isChecked: boolean }) => {
  if (props.isChecked) {
    return 'border/list-item-major-selected'
  }
  return 'border/list-item'
}

const getFocusBorder = (props: { isChecked: boolean }) => {
  if (props.isChecked) {
    return 'border/focus-indicator-item-selected'
  }
  return 'border/focus-indicator'
}
